'use client';

import DeleteIcon from '@mui/icons-material/Delete';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import {
	Button,
	Divider,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { InverseButton } from '@parkchirp/ui-library';
import React, { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react';
import { v4 } from 'uuid';

import { RateV2, SnackBarProps } from '../../../types';
import { Dayjs, dayjs } from '../../../utils';
import { getDiscountStartMinDate, validateDiscountPeriods } from './futureDiscountValidation';

interface ChildRateConfigProps {
	setTabValue: Dispatch<SetStateAction<string>>;
	missingFields: string;
	setMissingFields: Dispatch<SetStateAction<string>>;
	setActiveStep: Dispatch<SetStateAction<number>>;
	childStartDate: Date | string;
	setChildStartDate: Dispatch<SetStateAction<Date | string>>;
	childEndDate: Date | string | null;
	setChildEndDate: Dispatch<SetStateAction<Date | null>>;
	childDiscountType: string;
	setChildDiscountType: Dispatch<SetStateAction<string>>;
	childDiscountAmount: string | number | null;
	setChildDiscountAmount: Dispatch<SetStateAction<string | number | null>>;
	selectedRate: RateV2 | null;
	setMonthlyFee: Dispatch<SetStateAction<any>>;
	theme: any;
	onlineStart: Dayjs;
	onlineEnd: Dayjs;
	setShowSnackbar: Dispatch<SetStateAction<Partial<SnackBarProps> | null>>;
	online: string;
	futureDiscounts?: any[];
	setFutureDiscounts?: Dispatch<SetStateAction<any[]>>;
}

const FutureDiscountButton = ({
	handleAddDiscountClick,
	tooltipMessage = 'Schedule Future Discount',
	isDisabled = false,
}) => (
	<Tooltip placement='top' title={tooltipMessage}>
		<span>
			<IconButton disabled={isDisabled} size='large' onClick={() => handleAddDiscountClick()}>
				<MoreTimeIcon />
			</IconButton>
		</span>
	</Tooltip>
);

const DiscountAmountInput = ({ handleChange, hasError, fieldProps, value, isDisabled = false }) => (
	<TextField
		id='outlined-start-adornment'
		variant='outlined'
		fullWidth
		placeholder='Discount Amount'
		size='medium'
		error={hasError}
		type='number'
		onKeyDown={(event) => {
			if (event?.key === '-' || event?.key === '+' || event?.key === 'e') {
				event.preventDefault();
			}
		}}
		value={value}
		onChange={handleChange}
		InputLabelProps={{ shrink: true }}
		disabled={isDisabled}
		{...fieldProps}
	/>
);

const DateInput = ({ handleChange, hasError, value, isDisabled, placeholder = '', minDate = dayjs() }) => (
	<LocalizationProvider dateAdapter={AdapterDayjs}>
		<DatePicker<Dayjs>
			format='MM/DD/YYYY'
			value={value}
			onChange={handleChange}
			slotProps={{
				textField: {
					fullWidth: true,
					error: hasError,
					placeholder,
				},
			}}
			minDate={minDate}
			disabled={isDisabled}
		/>
	</LocalizationProvider>
);

const FutureDiscounts = ({
	futureDiscounts,
	handleDeleteDiscount,
	handleDiscountChange,
	childDiscountType,
	discountAmountInputProps,
	errors,
}) =>
	futureDiscounts.map((discount, index) => {
		const isDiscountEndDateInPast = !discount.isNew && discount.originalEndDatePassed;

		return (
			<Grid item xs={12} key={discount.reactKey}>
				<Grid container spacing={2}>
					<Grid item marginTop={1} xs={12}>
						{index === 0 ? <InputLabel>Future Discounts</InputLabel> : <Divider sx={{ borderWidth: 1 }} />}
					</Grid>
					<Grid item xs={12}>
						<Grid container spacing={1} display='flex' alignItems='center'>
							<Grid item xs={0.5}>
								<Typography variant='h6' color='textSecondary'>
									{index + 1}
								</Typography>
							</Grid>
							<Grid item xs={3}>
								<DateInput
									handleChange={(newValue) => {
										handleDiscountChange('startDate', index, newValue);
									}}
									hasError={errors[index]?.startDate}
									value={discount.startDate ? dayjs.utc(discount.startDate) : null}
									isDisabled={isDiscountEndDateInPast || index === 0 || (discount.isNew && index > 0)}
									placeholder='Start Date'
									minDate={getDiscountStartMinDate(discount)}
								/>
							</Grid>
							<Grid item xs={3}>
								<DateInput
									handleChange={(newValue) => {
										handleDiscountChange('endDate', index, newValue);
									}}
									hasError={errors[index]?.endDate}
									value={discount.endDate ? dayjs.utc(discount.endDate) : null}
									isDisabled={isDiscountEndDateInPast}
									placeholder='End Date'
									minDate={isDiscountEndDateInPast ? dayjs.utc(discount.endDate) : dayjs()}
								/>
							</Grid>
							<Grid item xs={5.5}>
								<Grid item xs={12}>
									<Grid container spacing={0.5} display='flex' alignItems='center'>
										<Grid item xs={10}>
											<DiscountAmountInput
												handleChange={(e) =>
													handleDiscountChange('discountAmount', index, e.target.value)
												}
												hasError={errors[index]?.discountAmount}
												value={discount.discountAmount}
												fieldProps={discountAmountInputProps}
												isDisabled={index === 0 || isDiscountEndDateInPast}
											/>
										</Grid>
										<Grid item xs={1}>
											<Tooltip placement='top' title='Delete Future Discount'>
												<span>
													<IconButton
														disabled={
															childDiscountType === 'FEE' || isDiscountEndDateInPast
														}
														size='large'
														onClick={() => {
															handleDeleteDiscount(index);
														}}
													>
														<DeleteIcon />
													</IconButton>
												</span>
											</Tooltip>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		);
	});

const ChildRateConfig: FC<ChildRateConfigProps> = ({
	setTabValue,
	childStartDate,
	setChildStartDate,
	childEndDate,
	setChildEndDate,
	childDiscountType,
	setChildDiscountType,
	childDiscountAmount,
	setChildDiscountAmount,
	setActiveStep,
	missingFields,
	setMissingFields,
	selectedRate,
	setMonthlyFee,
	theme,
	onlineStart,
	onlineEnd,
	setShowSnackbar,
	futureDiscounts = [],
	setFutureDiscounts,
	online,
}) => {
	const [futureDiscountErrors, setFutureDiscountErrors] = useState({});

	useEffect(() => {
		if (childDiscountType === 'FEE') {
			setFutureDiscounts([]);
			setFutureDiscountErrors({});
		}
	}, [childDiscountType, setFutureDiscounts]);

	const validateFutureDiscounts = () => {
		const errors = validateDiscountPeriods(
			futureDiscounts,
			childStartDate,
			childEndDate,
			dayjs().tz(selectedRate?.billingProfile?.facility?.timezone || 'America/Chicago'),
		);
		setFutureDiscountErrors(Object.fromEntries(errors.map((error, index) => [index, error])));
		const firstError = errors.find((error) => error.message);
		if (firstError) {
			setShowSnackbar({ message: firstError.message, severity: 'error' });
		}
		return errors.flatMap((error) =>
			(['startDate', 'endDate', 'discountAmount'] as const).filter((field) => error[field]),
		);
	};

	const handleNext = () => {
		const requiredFields: any = {};
		if (childDiscountType !== 'FEE') {
			requiredFields.childDiscountAmount = childDiscountAmount;
		}
		let errorMessage = '';
		Object.entries(requiredFields).forEach((e) => {
			const key = e[0];
			const value = e[1];
			if (!value && value !== 0) {
				const newValue = `${key}, `;
				errorMessage += newValue;
			}
		});

		if (
			childEndDate &&
			(dayjs(childEndDate).isBefore(dayjs(), 'day') || dayjs(childEndDate).isBefore(dayjs(childStartDate), 'day'))
		) {
			errorMessage += 'childEndDate, ';
		}

		let hasOnlineSalesDateError = false;
		if (dayjs(childStartDate).isAfter(onlineStart, 'day')) {
			hasOnlineSalesDateError = true;
		}
		if (childEndDate && dayjs(childEndDate).isBefore(onlineEnd, 'day')) {
			hasOnlineSalesDateError = true;
		}
		if (hasOnlineSalesDateError && online !== 'ADMIN_SIGN_UP_ONLY') {
			setShowSnackbar({
				message: `Your online sales dates are not within the child's dates. The child start must be before the online sales start and the child end (if provided) must be after the online sales end.`,
				severity: 'error',
			});
			return;
		}

		const futureDiscountValidationErrors = validateFutureDiscounts();
		errorMessage += futureDiscountValidationErrors.join(', ');

		if (errorMessage) {
			setMissingFields(errorMessage);
		} else {
			setMissingFields('');
			setActiveStep(3);
			setTabValue('4');
		}
	};

	const handleDiscountAmountChange = (e) => {
		const { value } = e.target;
		let regex;
		if (childDiscountType === 'PERCENT') {
			// Allow only numbers with up to 4 decimal places
			regex = /^(\d*\.)?\d{0,4}$/;
		} else {
			// Allow only numbers with up to 2 decimal places
			regex = /^\d+(\.\d{0,2})?$/;
		}
		if (regex.test(value) || value === '') {
			setChildDiscountAmount(value);
		}
	};

	const handleDiscountFeeTypeChange = (e) => {
		if (e.target.value !== 'FEE') {
			selectedRate.rateFees.forEach((rf) => {
				if (rf.fee.feeType.name === 'Monthly') {
					setMonthlyFee({ ...rf.fee, id: rf.feeId });
				}
			});
		}
		setChildDiscountType(e.target.value);
	};

	const handleAddFutureDiscount = () => {
		if (futureDiscounts.length === 0) {
			setFutureDiscounts([
				{
					id: null,
					rateId: selectedRate?.parentRateId || null,
					discountType: childDiscountType,
					discountAmount: childDiscountAmount,
					startDate: dayjs.utc(childStartDate).format('YYYY-MM-DD'),
					endDate: '',
					reactKey: v4(),
					isNew: true,
					originalEndDatePassed: false,
				},
			]);
		} else {
			setFutureDiscounts((prevDiscounts) => [
				...prevDiscounts,
				{
					id: null,
					rateId: selectedRate?.parentRateId || null,
					discountType: childDiscountType,
					discountAmount: '',
					startDate:
						prevDiscounts.length > 0
							? dayjs
									.utc(prevDiscounts[prevDiscounts.length - 1].endDate)
									.add(1, 'day')
									.format('YYYY-MM-DD')
							: '',
					endDate: '',
					reactKey: v4(),
					isNew: true,
					originalEndDatePassed: false,
				},
			]);
		}
	};

	const handleFutureDiscountChange = (field: string, index: number, value: string | Dayjs | null) => {
		const currDiscount = futureDiscounts[index];
		const updatedDiscount = {
			...currDiscount,
			[field]: value,
		};
		setFutureDiscounts((prevDiscounts) => [
			...prevDiscounts.slice(0, index),
			updatedDiscount,
			...prevDiscounts.slice(index + 1),
		]);

		if (field === 'endDate' && value && dayjs(value).isValid() && dayjs(value).isAfter(dayjs('1000-1-1'), 'year')) {
			if (futureDiscounts[index + 1]) {
				const nextDiscount = futureDiscounts[index + 1];
				nextDiscount.startDate = dayjs.utc(value).add(1, 'day').format('YYYY-MM-DD');

				setFutureDiscounts((prevDiscounts) => [
					...prevDiscounts.slice(0, index + 1),
					nextDiscount,
					...prevDiscounts.slice(index + 2),
				]);
			}
			if (!futureDiscounts[index + 1]) {
				handleAddFutureDiscount();
			}
		}
	};

	const handleDeleteFutureDiscount = (index: number) => {
		setFutureDiscounts((prevDiscounts) => {
			const updated = [...prevDiscounts.slice(0, index), ...prevDiscounts.slice(index + 1)];

			updated.forEach((discount, i) => {
				if (i > 0) {
					const prevDiscount = updated[i - 1];
					discount.startDate = dayjs.utc(prevDiscount.endDate).add(1, 'day').format('YYYY-MM-DD');
				}

				if (i === 0) {
					discount.startDate = dayjs.utc(childStartDate).format('YYYY-MM-DD');
					discount.discountAmount = childDiscountAmount;
				}

				if (i === updated.length - 1) {
					discount.endDate = '';
				}

				return discount;
			});

			return updated;
		});

		setFutureDiscountErrors({});
	};

	const discountAmountInputProps = useMemo(
		() => ({
			InputProps:
				childDiscountType === 'DOLLAR'
					? {
							startAdornment: <InputAdornment position='start'>$</InputAdornment>,
							sx: { paddingLeft: '5px' },
						}
					: childDiscountType === 'PERCENT'
						? {
								endAdornment: <InputAdornment position='end'>%</InputAdornment>,
								sx: { paddingLeft: '5px' },
							}
						: null,
			inputProps: {
				step: 0.01,
				min: 0,
				pattern: '[0-9]*',
				inputMode: 'numeric',
				'data-cy': 'fee-input-input',
			},
		}),
		[childDiscountType],
	);

	return (
		<Grid spacing={2} container>
			<Grid item xs={6}>
				<InputLabel>
					Child Start Date
					<span className='pca__ratesForm-required'>*</span>
				</InputLabel>
				<DateInput
					handleChange={(newValue) => setChildStartDate(newValue.toDate() || null)}
					hasError={missingFields.includes('childStart')}
					value={dayjs(childStartDate)}
					isDisabled={!!selectedRate.parentRateId}
					placeholder='Start Date'
				/>
			</Grid>
			<Grid item xs={6}>
				<InputLabel>Child End Date</InputLabel>
				<DateInput
					handleChange={(newValue) => setChildEndDate(newValue.toDate() || null)}
					hasError={missingFields.includes('childEndDate')}
					value={dayjs(childEndDate)}
					isDisabled={!!selectedRate.parentRateId && dayjs(childEndDate).isBefore(dayjs(), 'day')}
					placeholder='End Date'
				/>
			</Grid>
			<Grid item xs={6}>
				<InputLabel>
					Discount Type<span className='pca__ratesForm-required'>*</span>
				</InputLabel>
				<Select
					fullWidth
					value={childDiscountType}
					required
					disabled={!!selectedRate.parentRateId}
					error={missingFields.includes('discountType')}
					onChange={handleDiscountFeeTypeChange}
				>
					<MenuItem value='FEE'>Fee</MenuItem>
					<MenuItem value='DOLLAR'>Dollar</MenuItem>
					<MenuItem value='PERCENT'>Percent</MenuItem>
				</Select>
			</Grid>
			<Grid item xs={6}>
				<InputLabel>
					Discount Amount
					{childDiscountType === 'FEE' ? null : <span className='pca__ratesForm-required'>*</span>}
				</InputLabel>
				<Grid container xs={12} spacing={1} alignItems='center'>
					<Grid item xs={futureDiscounts.length ? 12 : 11}>
						<DiscountAmountInput
							handleChange={handleDiscountAmountChange}
							hasError={missingFields.includes('childDiscountAmount')}
							value={childDiscountAmount}
							isDisabled={!!selectedRate.parentRateId || childDiscountType === 'FEE'}
							fieldProps={discountAmountInputProps}
						/>
					</Grid>
					{futureDiscounts.length === 0 && (
						<Grid item xs={1}>
							<FutureDiscountButton
								handleAddDiscountClick={handleAddFutureDiscount}
								isDisabled={childDiscountType === 'FEE' || dayjs(childEndDate).isBefore(dayjs(), 'day')}
							/>
						</Grid>
					)}
				</Grid>
			</Grid>
			<Grid item xs={12}>
				<Grid container xs={12} spacing={1}>
					{futureDiscounts.length > 0 && (
						<FutureDiscounts
							futureDiscounts={futureDiscounts}
							handleDeleteDiscount={handleDeleteFutureDiscount}
							handleDiscountChange={handleFutureDiscountChange}
							childDiscountType={childDiscountType}
							errors={futureDiscountErrors}
							discountAmountInputProps={discountAmountInputProps}
						/>
					)}
				</Grid>
			</Grid>
			<Grid className='pca__ratesForm-containerPadding' spacing={2} container>
				<Grid item xs={6}>
					<InverseButton
						onClick={() => {
							setTabValue('2');
							setActiveStep(1);
						}}
						data-cy='previous-button'
						fullWidth
						name='Previous'
						theme={theme}
					/>
				</Grid>
				<Grid item xs={6}>
					<Button fullWidth onClick={handleNext} data-cy='next-step-button'>
						Next
					</Button>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default ChildRateConfig;
